import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { createPortal } from "react-dom";
import type { AMapMap, AMapMarker, AMapNamespace } from "@/features/world_map/amapLoader";
import { ensureCopyright } from "@/features/world_map/attribution";
import { PAN_MS, type MapCamera } from "@/shared/map/mapCamera";
import { toAmapLngLat } from "@/features/world_map/coords";
import type { MapConfigState } from "@/features/world_map/mapConfig";
import { readMapSession, writeMapSession } from "@/features/world_map/mapSession";
import type { GlobeCluster, GlobeRegion } from "./globeTypes";
import { PUBLIC_MAP_SCOPE } from "./mapDeps";
import { regionFocus } from "./publicMapModel";
import { roadOf } from "./RegionLayer";
import { SpotPin } from "./SpotMarker";
import { EventSpot } from "./RegionLayer";

/**
 * 地区层的真实底图：高德 JS API 2.0（公开的 /map/config → world_map 的 amapLoader → 站点根的 /_AMapService 安全代理）。
 * 不直接用 world_map 的 AmapView：它画的是自家宠物（家、路线、跟随），这里只画公开居民的头像；加载、坐标换算、署名照它的做法。
 * - 公开坐标是 WGS-84，画到高德上之前换成 GCJ-02（world_map/coords.toAmapLngLat）；同一坐标的几位合成一个标记，不打散；
 * - 头像标记用 Marker 的 DOM 内容 + React portal（和 AmapView 一样），点了交给页面出短卡 / 名单；
 * - 镜头：第一次按这一片的居民装进屏幕（setFitView）；拖动、缩放停下记进 world_map/mapSession 的 "public" 作用域，回来接着看；
 * - 加载失败交给上层（onFail），上层退回示意图。高德 Logo 与版权抬到底边那条“新鲜事”之上（public-map.css）。
 */
type Overlay = { marker: AMapMarker; el: HTMLDivElement };

/** 第一次取景时四边让开的像素，按高德 avoid 的顺序：上、下、左、右（见 setFitView 那一行） */
const REGION_AMAP_AVOID: [number, number, number, number] = [176, 100, 56, 64];

/**
 * 标记放在哪（UI2-CITY-ACTORS-117）：在路上的一位放在两片区中心之间、按计划走到的那一成（和示意图上 routePoint 同一个进度，城市尺度上
 * 经纬度直线和屏幕直线差不到一像素）；进度读不出时停在起点那头。其余、或没给时刻（只有直接渲染本组件的老用例不给），照旧放在公开坐标上。
 */
export function markerAt(cluster: GlobeCluster, nowMs: number | undefined): { lat: number; lng: number } {
  const road = nowMs === undefined ? null : roadOf(cluster, nowMs);
  if (!road) return cluster;
  const p = road.progress ?? 0;
  const { from, to } = road.ends;
  return { lat: from.lat + (to.lat - from.lat) * p, lng: from.lng + (to.lng - from.lng) * p };
}

export function RegionAmap({
  config,
  loadMap,
  clusters,
  region,
  regionKey,
  openKey,
  onActivate,
  onFail,
  spot = null,
  nowMs,
  paused = false,
  camera,
}: {
  config: MapConfigState;
  loadMap: (config: MapConfigState) => Promise<AMapNamespace>;
  clusters: readonly GlobeCluster[];
  region: GlobeRegion;
  /** 这一片的标识：镜头只在同一片里接着看 */
  regionKey: string;
  openKey: string | null;
  onActivate: (cluster: GlobeCluster, from: HTMLElement) => void;
  onFail: (reason: string) => void;
  spot?: { lat: number; lng: number; label: string } | null;
  /** 城市层的时刻（RegionLayer 的分钟钟）：在路上的几位按它放 */
  nowMs?: number;
  /** 页面有面板盖着地图：小角色停在当下 */
  paused?: boolean;
  /** 只挪镜头的口子（UI-WORLD-LIVING-117-2b）：按像素挪，换算成镜头中心的经纬度（GCJ-02，当前缩放下每像素多少米）再 panTo；直接跳到位用 setZoomAndCenter */
  camera?: Ref<MapCamera>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ AMap: AMapNamespace; map: AMapMap; restored: boolean; framed: boolean } | null>(null);
  const overlays = useRef(new Map<string, Overlay>());
  const spotRef = useRef<Overlay | null>(null);
  const [ready, setReady] = useState(false);
  const [, setVersion] = useState(0);
  const failRef = useRef(onFail);
  useImperativeHandle(camera, () => ({
    // 底图还没读好时挪不了（reach 是 0）：地图那边等它读好再放一次（评审 R2 高）
    reach: () => (mapRef.current?.map ? Number.POSITIVE_INFINITY : 0),
    panBy: (dx: number, dy: number, instant: boolean) => {
      const map = mapRef.current?.map;
      if (!map) return;
      // 画面内容往下挪 dy＝镜头中心往北挪；往右挪 dx＝往西挪
      const center = map.getCenter();
      const metres = map.getResolution();
      const lat = center.lat + (dy * metres) / 111_320;
      const lng = center.lng - (dx * metres) / (111_320 * Math.cos((center.lat * Math.PI) / 180));
      if (instant) map.setZoomAndCenter(map.getZoom(), [lng, lat], true);
      else map.panTo([lng, lat], PAN_MS);
    },
  }), []);
  useEffect(() => {
    failRef.current = onFail;
  });

  useEffect(() => {
    let disposed = false;
    const current = overlays.current;
    loadMap(config)
      .then((AMap) => {
        const container = containerRef.current;
        if (disposed || !container) return;
        const saved = readMapSession(PUBLIC_MAP_SCOPE).camera;
        const restore = saved && saved.focusId === regionKey ? saved : null;
        const map = new AMap.Map(container, {
          zoom: restore?.zoom ?? 11,
          center: restore?.center ?? toAmapLngLat(region.center),
          viewMode: "2D",
          resizeEnable: true,
          // UI-THEME-DARK-02：不切深色，一直用浅色底图（后端 style_dark 留着不读）
          mapStyle: config.style ?? undefined,
          showIndoorMap: false,
          jogEnable: true,
        });
        const report = () => {
          const center = map.getCenter();
          writeMapSession(PUBLIC_MAP_SCOPE, { camera: { focusId: regionKey, center: [center.lng, center.lat], zoom: map.getZoom() } });
        };
        map.on("moveend", report);
        map.on("zoomend", report);
        ensureCopyright(container);
        map.on("complete", () => ensureCopyright(container));
        mapRef.current = { AMap, map, restored: Boolean(restore), framed: Boolean(restore) };
        setReady(true);
      })
      .catch((error: unknown) => {
        if (!disposed) failRef.current(error instanceof Error ? error.message : "map_load_failed");
      });
    return () => {
      disposed = true;
      current.clear();
      spotRef.current = null;
      mapRef.current?.map.destroy();
      mapRef.current = null;
    };
  }, [config, loadMap, regionKey, region]);

  // 标记：按坐标那一格建 / 删；位置用 GCJ-02；第一次建好后把这一片装进屏幕。
  // 在路上的几位（UI2-CITY-ACTORS-117）：和示意图同一个进度，放在两片区中心之间；真底图上先不画线（要用高德自己的折线，这里看不了真底图）
  const keys = clusters.map((cluster) => cluster.key).join("|");
  useEffect(() => {
    const current = mapRef.current;
    if (!ready || !current) return;
    const { AMap, map } = current;
    const wanted = new Map(clusters.map((cluster) => [cluster.key, cluster] as const));
    let changed = false;
    for (const [key, overlay] of overlays.current) {
      if (wanted.has(key)) continue;
      map.remove(overlay.marker);
      overlays.current.delete(key);
      changed = true;
    }
    for (const cluster of clusters) {
      const existing = overlays.current.get(cluster.key);
      if (existing) {
        existing.marker.setPosition(toAmapLngLat(markerAt(cluster, nowMs)));
        continue;
      }
      const el = document.createElement("div");
      el.className = "ps-pmap-amap__anchor";
      const marker = new AMap.Marker({ position: toAmapLngLat(markerAt(cluster, nowMs)), content: el, anchor: "bottom-center", zIndex: 120, bubble: false });
      map.add(marker);
      overlays.current.set(cluster.key, { marker, el });
      changed = true;
    }
    // 发生地标记（从新鲜事点进来）
    if (spot && !spotRef.current) {
      const el = document.createElement("div");
      el.className = "ps-pmap-amap__spot";
      const marker = new AMap.Marker({ position: toAmapLngLat(spot), content: el, anchor: "center", zIndex: 100, bubble: true });
      map.add(marker);
      spotRef.current = { marker, el };
      changed = true;
    }
    if (!current.framed) {
      const focus = regionFocus(clusters, region).map((cluster) => overlays.current.get(cluster.key)?.marker).filter((m): m is AMapMarker => Boolean(m));
      if (spotRef.current) focus.push(spotRef.current.marker);
      // 高德 avoid 的顺序是「上、下、左、右」（不是 CSS 的上右下左）。四边和示意图那边同一来源（UI2-WORLD-MAP-4CD-01 的 REGION_MAP_INSETS 合计）：
      // 上 176（顶栏两行、缩放键，加头像针往上占的 60～79）、下 100（署名和新鲜事条）、左 56、右 64（针宽一半加角标）（UI-WORLD-FOLLOWUP-01）
      if (focus.length) map.setFitView(focus, true, REGION_AMAP_AVOID, 16);
      else map.setZoomAndCenter(9, toAmapLngLat(region.center), true);
      current.framed = true;
    }
    if (changed) setVersion((v) => v + 1);
    // keys 概括了成员变化
  }, [ready, keys, clusters, region, spot, nowMs]);

  useEffect(() => {
    for (const [key, overlay] of overlays.current) overlay.marker.setzIndex(key === openKey ? 140 : 120);
  }, [openKey]);

  return (
    <div className="ps-pmap-amap" ref={containerRef} data-testid="region-amap">
      {ready
        ? clusters.map((cluster) => {
            const overlay = overlays.current.get(cluster.key);
            return overlay ? createPortal(<SpotPin cluster={cluster} expanded={cluster.key === openKey} onActivate={onActivate} paused={paused} />, overlay.el, cluster.key) : null;
          })
        : null}
      {ready && spot && spotRef.current ? createPortal(<EventSpot label={spot.label} />, spotRef.current.el) : null}
    </div>
  );
}
